import { gql } from '@apollo/client';

const financialAccountFragment = gql`
  fragment FinancialAccountFragment on FinancialAccount {
    autoRefreshRequired
    balance
    createdAt
    currency
    extra
    financialPortal {
      accountsState
      activeMigration
      alertMessage
      alertName
      alertPriority
      authState
      authStateSetAt
      canonicalInstitutionId
      createdAt
      errorMessage
      errorName
      errorPriority
      id
      identityId
      isActiveSync
      isAutoRefreshing
      isBalanceRefreshing
      isManualRefreshing
      lastRefreshedAt
      marketplaceInstitution {
        iavAdminDeactivated
        iavEnabled
        id
        loginUrl
        logoUrl
        name
      }
      primarySource
      sequenceNumber
      source
      sourceEntityId
      sourceInstitutionId
      transactionsState
      updatedAt
    }
    iavVerified
    id
    isHidden
    lastSyncedAt
    mask
    name
    primarySource
    status
    subtype
    subtypeDetails
    transactionsSyncedAt
    type
    updatedAt
    verificationState
    deleteQueuedAt
    flaggedForClosureAt
  }
`;

const financialPortalFragment = gql`
  fragment FinancialPortalFragment on FinancialPortal {
    createdAt
    id
    identityId
    primarySource
    sequenceNumber
    source
    sourceEntityId
    updatedAt
    accountsState
    activeMigration
    alertMessage
    alertName
    alertPriority
    authState
    authStateSetAt
    deleteQueuedAt
    canonicalInstitutionId
    errorMessage
    errorName
    errorPriority
    isActiveSync
    isAutoRefreshing
    isBalanceRefreshing
    isManualRefreshing
    isRefreshing
    lastRefreshedAt
    migrationDetails {
      hasUserConfirmed
    }
    marketplaceInstitution {
      id
      name
      loginUrl
      logoUrl
      iavEnabled
      iavAdminDeactivated
      externalId
      aggregatorType
    }
    primaryClientAction
    sourceInstitutionId
    transactionsState
  }
`;

const financialAccountsForUserFragment = gql`
  fragment FinancialAccountsForUserFragment on Query {
    financialAccounts {
      accounts {
        ...FinancialAccountFragment
      }
    }
  }
  ${financialAccountFragment}
`;

const linkedFinancialPortalsForUserFragment = gql`
  fragment LinkedFinancialPortalsForUserFragment on Query {
    linkedFinancialPortals {
      portals {
        ...FinancialPortalFragment
      }
    }
  }
  ${financialPortalFragment}
`;

const financialAccountTypeSumsFragment = gql`
  fragment FinancialAccountTypeSumsFragment on Query {
    financialAccountTypeSums {
      assets
      banks
      bills
      creditCards
      debts
      investments
      loans
      other
    }
  }
`;

export const GET_FINANCIAL_DATA_PROFILE_FOR_USER = gql`
  query GetFinancialDataProfileForUser {
    ...FinancialAccountsForUserFragment
    ...LinkedFinancialPortalsForUserFragment
    ...FinancialAccountTypeSumsFragment
  }
  ${financialAccountsForUserFragment}
  ${linkedFinancialPortalsForUserFragment}
  ${financialAccountTypeSumsFragment}
`;

export const FINANCIAL_TRANSACTIONS_PROJECTION_FOR_MONTH = gql`
  query GetFinancialTransactionsProjectionForMonth($dateInTargetMonth: Date!) {
    financialTransactionsProjectionForMonth(
      dateInTargetMonth: $dateInTargetMonth
    ) {
      earning
    }
  }
`;

export const FINANCIAL_TRANSACTIONS_TOP_CATEGORIES_SPENDING = gql`
  query GetFinancialTransactionsTopCategoriesBySpending(
    $dateInTargetStartMonth: Date!
    $dateInTargetEndMonth: Date!
  ) {
    financialTransactionsTopCategoriesBySpending(
      dateInTargetStartMonth: $dateInTargetStartMonth
      dateInTargetEndMonth: $dateInTargetEndMonth
    ) {
      topCategoriesBySpending {
        amount
        category
        categoryId
      }
    }
  }
`;

export const FINANCIAL_TRANSACTIONS_AVERAGE_SPENDING_BY_DAY_OF_THE_MONTH = gql`
  query GetFinancialTransactionsAverageSpendingByDayOfTheMonth(
    $dateInTargetStartMonth: Date!
    $dateInTargetEndMonth: Date!
  ) {
    financialTransactionsAverageSpendingByDayOfTheMonth(
      dateInTargetStartMonth: $dateInTargetStartMonth
      dateInTargetEndMonth: $dateInTargetEndMonth
    ) {
      averageSpendingByDayOfTheMonth {
        amount
        dayOfTheMonth
      }
    }
  }
`;

export const FINANCIAL_TRANSACTIONS_SPENDING_BY_DATE_FOR_MONTH = gql`
  query GetFinancialTransactionsSpendingByDateForMonth(
    $dateInTargetMonth: Date!
  ) {
    financialTransactionsSpendingByDateForMonth(
      dateInTargetMonth: $dateInTargetMonth
    ) {
      spendingByDayOfTheMonthForPriorMonth {
        dayOfTheMonth
        amount
      }
      spendingByDayOfTheMonthForTargetMonth {
        dayOfTheMonth
        amount
      }
    }
  }
`;

export const FINANCIAL_TRANSACTIONS_TOP_MERCHANT_SPENDING = gql`
  query GetFinancialTransactionsTopMerchantsBySpending(
    $dateInTargetStartMonth: Date!
    $dateInTargetEndMonth: Date!
    $pagination: AdvancedOffsetPaginationInput
  ) {
    financialTransactionsTopMerchantsBySpending(
      dateInTargetStartMonth: $dateInTargetStartMonth
      dateInTargetEndMonth: $dateInTargetEndMonth
      pagination: $pagination
    ) {
      topMerchantsBySpending {
        amount
        merchantName
      }
    }
  }
`;

export const FINANCIAL_TRANSACTIONS_SPENDING_AND_EARNING_FOR_MONTH_RANGE = gql`
  query GetFinancialTransactionsSpendingAndEarningForMonthRange(
    $dateInStartMonth: Date!
    $dateInEndMonth: Date!
    $filter: FinancialTransactionsFilterInput
  ) {
    financialTransactionsSpendingAndEarningForMonthRange(
      dateInStartMonth: $dateInStartMonth
      dateInEndMonth: $dateInEndMonth
      filter: $filter
    ) {
      spendingAndEarningForMonthRange {
        spending
        earning
      }
    }
  }
`;
